/**
 * Pure appearance-settings model for the standalone app's shared appearance
 * panel. Kept renderer-agnostic and DOM-free so it can be unit-tested and so
 * `app.ts` stays thin glue: this module owns the state shape, the validation of
 * persisted (untrusted) values, and the dispatch to a renderer's duck-typed
 * appearance setters. The panel that phylocanvas embeds in its own gear-menu is
 * provided here for every other renderer instead (see
 * `rendererProvidesOwnAppearanceUI`).
 */

/** Layout options the panel offers, mirroring the Tableau dialog's TreeTypes. */
export const APPEARANCE_LAYOUTS = [
  { code: 'rc', label: 'Rectangular' },
  { code: 'cr', label: 'Circular' },
  { code: 'rd', label: 'Radial' },
  { code: 'dg', label: 'Diagonal' },
  { code: 'hr', label: 'Hierarchical' },
] as const;

export type LayoutCode = (typeof APPEARANCE_LAYOUTS)[number]['code'];

const LAYOUT_CODES: readonly string[] = APPEARANCE_LAYOUTS.map((l) => l.code);

/**
 * Leaf-marker shapes the panel offers, a curated subset of the library's much
 * larger shape registry (`vendor/theiaphyloviewer.svg/src/render/shapes.js`).
 * Each `code` is the exact string the library's `nodeShape` prop expects;
 * `label` is the panel's display text. Kept small and recognisable rather than
 * surfacing every polygon/star/chevron the registry defines.
 */
export const APPEARANCE_NODE_SHAPES = [
  { code: 'circle', label: 'Circle' },
  { code: 'square', label: 'Square' },
  { code: 'diamond', label: 'Diamond' },
  { code: 'triangle', label: 'Triangle' },
  { code: 'star', label: 'Star' },
  { code: 'hexagon', label: 'Hexagon' },
  { code: 'cross', label: 'Cross' },
  { code: 'plus', label: 'Plus' },
  { code: 'wye', label: 'Wye' },
] as const;

export type NodeShapeCode = (typeof APPEARANCE_NODE_SHAPES)[number]['code'];

const NODE_SHAPE_CODES: readonly string[] = APPEARANCE_NODE_SHAPES.map((s) => s.code);

/**
 * Leaf-label font families the panel offers, the CSS generic families plus
 * `system-ui`, all web-safe so no font has to ship. Each `code` is passed
 * verbatim to the library's `fontFamily` prop (which becomes the labels'
 * `font-family`); `label` is the panel's display text.
 */
export const APPEARANCE_FONT_FAMILIES = [
  { code: 'monospace', label: 'Monospace' },
  { code: 'sans-serif', label: 'Sans-serif' },
  { code: 'serif', label: 'Serif' },
  { code: 'system-ui', label: 'System UI' },
  { code: 'cursive', label: 'Cursive' },
] as const;

export type FontFamilyCode = (typeof APPEARANCE_FONT_FAMILIES)[number]['code'];

const FONT_FAMILY_CODES: readonly string[] = APPEARANCE_FONT_FAMILIES.map((f) => f.code);

/** A `#rrggbb` colour, the form an `<input type="color">` emits and persists. */
const HEX_COLOUR_RE = /^#[0-9a-f]{6}$/i;

/** Slider bounds, shared by the panel inputs and the persisted-value validator. */
export const NODE_SIZE_RANGE = { min: 2, max: 40 } as const;
export const LABEL_SIZE_RANGE = { min: 6, max: 48 } as const;

/**
 * Bounds for the halo (highlight ring) geometry sliders, in the library's
 * logical tree-coordinate units. `haloRadius` is the ring radius around a
 * selected/highlighted node (default 12); `haloWidth` is its stroke width
 * (default 4). Both round-trip through `AppearanceState` like the size sliders.
 */
export const HALO_RADIUS_RANGE = { min: 0, max: 40 } as const;
export const HALO_WIDTH_RANGE = { min: 0, max: 20 } as const;

/**
 * Bounds for the canvas padding slider, in pixels. `padding` is the inset the
 * library leaves around the tree's drawing area (`core/index.js` subtracts
 * `2 * padding` from the viewport before scaling the tree into it), so a larger
 * value gives the tree more breathing room from the edges. Default 16.
 */
export const PADDING_RANGE = { min: 0, max: 100 } as const;

/**
 * Bounds for the metadata-block size slider, in pixels. `blockSize` is the edge
 * length of each metadata heat-map cell (`render/metadata.js` uses it as the
 * column `blockWidth`, capped by the per-leaf step size on orthogonal layouts).
 * Default 16.
 */
export const BLOCK_SIZE_RANGE = { min: 2, max: 48 } as const;

/**
 * Bounds for the scalebar customisation sliders. `scalebarDigits` is the extra
 * decimal precision added to the auto-computed minimum (`render/scalebar.js`
 * `scalebarMetrics`, default 2); `scalebarWidth` is the bar box width in px
 * (default 96); `scalebarFontSize` is the label size in px (default 13);
 * `scalebarOpacity` is the background box's fill-opacity, 0…1 (default 0.87).
 * A curated subset of the library's full scalebar prop set, height, line width,
 * padding and the position offsets are left at their defaults, and the position
 * *corner* is intentionally not surfaced because `renderScalebar` honours only
 * the `left`/`bottom` offsets (a corner dropdown would no-op three of four
 * choices). Each round-trips through `AppearanceState` like the size sliders.
 */
export const SCALEBAR_DIGITS_RANGE = { min: 0, max: 6 } as const;
export const SCALEBAR_WIDTH_RANGE = { min: 40, max: 200 } as const;
export const SCALEBAR_FONT_SIZE_RANGE = { min: 6, max: 24 } as const;
export const SCALEBAR_OPACITY_RANGE = { min: 0, max: 1 } as const;

/**
 * Bounds for the uniform-zoom slider, in the library's log2 zoom units (0 ==
 * fit-to-content, +1 == 2x in). −2…+4 spans 0.25x out to 16x in. Unlike the
 * size sliders this is *not* a persisted appearance value, zoom is transient
 * camera state (like pan), so the slider drives the renderer live and resets to
 * fit on each render rather than saving into `AppearanceState`.
 */
export const ZOOM_RANGE = { min: -2, max: 4 } as const;

/**
 * Bounds for the independent branch-axis and step-axis (leaf-spacing) zoom
 * sliders, in the same log2 units as `ZOOM_RANGE`. Unlike the uniform zoom these
 * *are* persisted appearance values: the library's `setBranchZoom`/`setStepZoom`
 * set props (not the camera), so they describe the tree's layout rather than the
 * transient view, and round-trip through `AppearanceState` like the size sliders.
 */
export const BRANCH_ZOOM_RANGE = { min: -2, max: 4 } as const;
export const STEP_ZOOM_RANGE = { min: -2, max: 4 } as const;

/**
 * Human-facing multiplier label for a log2 zoom level (the slider's read-out):
 * `2 ** zoom`, to one decimal, suffixed with `×`. Zoom 0 → `1.0×`, +1 → `2.0×`,
 * −1 → `0.5×`.
 */
export function formatZoomMultiplier(zoom: number): string {
  return `${(2 ** zoom).toFixed(1)}×`;
}

export interface AppearanceState {
  layout: LayoutCode;
  leafLabels: boolean;
  alignLabels: boolean;
  internalLabels: boolean;
  /** Internal-label font size in px (independent of the leaf-label size). */
  internalLabelSize: number;
  /** Lower bound of the internal-label value filter; `null` = unbounded. */
  internalLabelMin: number | null;
  /** Upper bound of the internal-label value filter; `null` = unbounded. */
  internalLabelMax: number | null;
  colourEdges: boolean;
  leafBorders: boolean;
  scalebar: boolean;
  /** Composition pie charts on collapsed internal nodes. */
  showPiecharts: boolean;
  nodeSize: number;
  labelSize: number;
  /** Leaf-marker shape; one of the library's `nodeShape` values. */
  nodeShape: NodeShapeCode;
  /** Leaf-label font family; one of the library's `fontFamily` values. */
  fontFamily: FontFamilyCode;
  /** Leaf-label colour as `#rrggbb`; converted to the library's RGBA at the renderer. */
  fontColour: string;
  /** Highlight/selection halo colour as `#rrggbb`; converted to RGBA at the renderer. */
  haloColour: string;
  /** Halo ring radius in logical tree units. */
  haloRadius: number;
  /** Halo ring stroke width in logical tree units. */
  haloWidth: number;
  /** Canvas padding (inset around the tree's drawing area) in pixels. */
  padding: number;
  /** Metadata heat-map block edge length in pixels. */
  blockSize: number;
  /** Extra scalebar decimal precision, added to the auto-computed minimum. */
  scalebarDigits: number;
  /** Scalebar box width in pixels. */
  scalebarWidth: number;
  /** Scalebar label font size in pixels. */
  scalebarFontSize: number;
  /** Scalebar background box fill-opacity, 0…1. */
  scalebarOpacity: number;
  /** Scalebar ink (bar + label) colour as `#rrggbb`; converted to RGBA at the renderer. */
  scalebarColour: string;
  /** Independent branch-axis zoom exponent (`scale ∝ 2 ** branchZoom`); 0 = none. */
  branchZoom: number;
  /** Independent step-axis (leaf-spacing) zoom exponent; 0 = none. */
  stepZoom: number;
  /** Metadata field whose values colour the leaf markers; `null` = no colour-by. */
  colorByField: string | null;
  /** Metadata fields rendered as heat-map blocks beside the tips; empty = none. */
  selectedMetadataFields: string[];
}

/**
 * Defaults mirror the SVG adapter's *effective* initial props, the values
 * `buildInitialProps` sets explicitly (leaf labels on, colour edges on) plus the
 * library defaults it leaves untouched (node size 14, label size 16, scalebar
 * on). Because the panel starts here, the panel reflects what the user already
 * sees and re-applying the defaults changes nothing.
 */
export const DEFAULT_APPEARANCE: AppearanceState = {
  layout: 'rc',
  leafLabels: true,
  // Aligned by default (diverges from the library's own alignLabels:false) so
  // tip labels read as a tidy column instead of a ragged, branch-tip edge.
  alignLabels: true,
  internalLabels: false,
  internalLabelSize: 16,
  internalLabelMin: null,
  internalLabelMax: null,
  colourEdges: true,
  leafBorders: false,
  scalebar: true,
  showPiecharts: false,
  nodeSize: 14,
  labelSize: 16,
  nodeShape: 'circle',
  fontFamily: 'monospace',
  fontColour: '#333333',
  haloColour: '#3c7383',
  haloRadius: 12,
  haloWidth: 4,
  padding: 16,
  blockSize: 16,
  scalebarDigits: 2,
  scalebarWidth: 96,
  scalebarFontSize: 13,
  scalebarOpacity: 0.87,
  scalebarColour: '#222222',
  branchZoom: 0,
  stepZoom: 0,
  colorByField: null,
  selectedMetadataFields: [],
};

/**
 * Structural view of the renderer setters this module drives. Every method is
 * optional so each call can be feature-detected: a renderer may implement only
 * some setters, and the active renderer reference may be null.
 */
export interface AppearanceTarget {
  changeTreeLayout?(code: string): void;
  setNodeShape?(shape: string): void;
  setFontFamily?(family: string): void;
  setFontColour?(hex: string): void;
  setHaloColour?(hex: string): void;
  setHaloRadius?(radius: number): void;
  setHaloWidth?(width: number): void;
  setPadding?(padding: number): void;
  setBlockSize?(size: number): void;
  setScalebarDigits?(digits: number): void;
  setScalebarWidth?(width: number): void;
  setScalebarFontSize?(size: number): void;
  setScalebarOpacity?(opacity: number): void;
  setScalebarColour?(hex: string): void;
  setNodeSize?(size: number): void;
  setLabelSize?(size: number): void;
  toggleLeafLabels?(on: boolean): void;
  toggleLabelAlignment?(on: boolean): void;
  toggleInternalLabels?(on: boolean): void;
  setInternalLabelSize?(size: number): void;
  setInternalLabelFilter?(min: number | null, max: number | null): void;
  toggleColourEdges?(on: boolean): void;
  toggleLeafBorders?(on: boolean): void;
  toggleScalebar?(on: boolean): void;
  togglePiecharts?(on: boolean): void;
  setBranchZoom?(zoom: number): void;
  setStepZoom?(zoom: number): void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function coerceSize(
  value: unknown,
  fallback: number,
  range: { min: number; max: number },
): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? clamp(value, range.min, range.max)
    : fallback;
}

function coerceBool(value: unknown, fallback: boolean): boolean {
  return value === undefined ? fallback : Boolean(value);
}

function coerceHexColour(value: unknown, fallback: string): string {
  return typeof value === 'string' && HEX_COLOUR_RE.test(value) ? value : fallback;
}

/** A finite number passes through; anything else (absent, NaN, non-number) is `null`. */
function coerceNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Coerce a persisted value into a list of field names: non-arrays become `[]`,
 * non-string entries are dropped. When `available` is supplied the result is
 * intersected with it so stale field names (renamed/removed columns) fall away;
 * when it is `undefined` the field list is simply unknown, so every string is
 * kept. An empty `available` is a known-empty list, not "unknown", so it filters
 * everything out.
 */
function coerceStringArray(value: unknown, available?: readonly string[]): string[] {
  if (!Array.isArray(value)) return [];
  const strings = value.filter((entry): entry is string => typeof entry === 'string');
  if (available === undefined) return strings;
  const allow = new Set(available);
  return strings.filter((field) => allow.has(field));
}

/**
 * Coerce a persisted (and therefore untrusted) appearance bag into a valid
 * `AppearanceState`: unknown layout codes, out-of-range or non-numeric sizes,
 * and absent keys all fall back to the defaults. Returns a fresh object and
 * never mutates the input. When `available` is supplied, persisted
 * `selectedMetadataFields` are intersected with it so field names that no longer
 * exist in the current data are dropped.
 */
export function mergeAppearance(saved: unknown, available?: readonly string[]): AppearanceState {
  const bag = (saved && typeof saved === 'object' ? saved : {}) as Record<string, unknown>;
  const layout =
    typeof bag.layout === 'string' && LAYOUT_CODES.includes(bag.layout)
      ? (bag.layout as LayoutCode)
      : DEFAULT_APPEARANCE.layout;
  const nodeShape =
    typeof bag.nodeShape === 'string' && NODE_SHAPE_CODES.includes(bag.nodeShape)
      ? (bag.nodeShape as NodeShapeCode)
      : DEFAULT_APPEARANCE.nodeShape;
  const fontFamily =
    typeof bag.fontFamily === 'string' && FONT_FAMILY_CODES.includes(bag.fontFamily)
      ? (bag.fontFamily as FontFamilyCode)
      : DEFAULT_APPEARANCE.fontFamily;
  return {
    layout,
    nodeShape,
    fontFamily,
    fontColour: coerceHexColour(bag.fontColour, DEFAULT_APPEARANCE.fontColour),
    haloColour: coerceHexColour(bag.haloColour, DEFAULT_APPEARANCE.haloColour),
    haloRadius: coerceSize(bag.haloRadius, DEFAULT_APPEARANCE.haloRadius, HALO_RADIUS_RANGE),
    haloWidth: coerceSize(bag.haloWidth, DEFAULT_APPEARANCE.haloWidth, HALO_WIDTH_RANGE),
    padding: coerceSize(bag.padding, DEFAULT_APPEARANCE.padding, PADDING_RANGE),
    blockSize: coerceSize(bag.blockSize, DEFAULT_APPEARANCE.blockSize, BLOCK_SIZE_RANGE),
    scalebarDigits: coerceSize(
      bag.scalebarDigits,
      DEFAULT_APPEARANCE.scalebarDigits,
      SCALEBAR_DIGITS_RANGE,
    ),
    scalebarWidth: coerceSize(
      bag.scalebarWidth,
      DEFAULT_APPEARANCE.scalebarWidth,
      SCALEBAR_WIDTH_RANGE,
    ),
    scalebarFontSize: coerceSize(
      bag.scalebarFontSize,
      DEFAULT_APPEARANCE.scalebarFontSize,
      SCALEBAR_FONT_SIZE_RANGE,
    ),
    scalebarOpacity: coerceSize(
      bag.scalebarOpacity,
      DEFAULT_APPEARANCE.scalebarOpacity,
      SCALEBAR_OPACITY_RANGE,
    ),
    scalebarColour: coerceHexColour(bag.scalebarColour, DEFAULT_APPEARANCE.scalebarColour),
    leafLabels: coerceBool(bag.leafLabels, DEFAULT_APPEARANCE.leafLabels),
    alignLabels: coerceBool(bag.alignLabels, DEFAULT_APPEARANCE.alignLabels),
    internalLabels: coerceBool(bag.internalLabels, DEFAULT_APPEARANCE.internalLabels),
    internalLabelSize: coerceSize(
      bag.internalLabelSize,
      DEFAULT_APPEARANCE.internalLabelSize,
      LABEL_SIZE_RANGE,
    ),
    internalLabelMin: coerceNullableNumber(bag.internalLabelMin),
    internalLabelMax: coerceNullableNumber(bag.internalLabelMax),
    colourEdges: coerceBool(bag.colourEdges, DEFAULT_APPEARANCE.colourEdges),
    leafBorders: coerceBool(bag.leafBorders, DEFAULT_APPEARANCE.leafBorders),
    scalebar: coerceBool(bag.scalebar, DEFAULT_APPEARANCE.scalebar),
    showPiecharts: coerceBool(bag.showPiecharts, DEFAULT_APPEARANCE.showPiecharts),
    nodeSize: coerceSize(bag.nodeSize, DEFAULT_APPEARANCE.nodeSize, NODE_SIZE_RANGE),
    labelSize: coerceSize(bag.labelSize, DEFAULT_APPEARANCE.labelSize, LABEL_SIZE_RANGE),
    branchZoom: coerceSize(bag.branchZoom, DEFAULT_APPEARANCE.branchZoom, BRANCH_ZOOM_RANGE),
    stepZoom: coerceSize(bag.stepZoom, DEFAULT_APPEARANCE.stepZoom, STEP_ZOOM_RANGE),
    colorByField:
      typeof bag.colorByField === 'string' && bag.colorByField !== ''
        ? bag.colorByField
        : DEFAULT_APPEARANCE.colorByField,
    selectedMetadataFields: coerceStringArray(bag.selectedMetadataFields, available),
  };
}

/**
 * Push an appearance state onto a renderer through its duck-typed setters. Each
 * call is feature-detected, so a renderer that implements only some setters (or
 * a null renderer) is handled without throwing.
 */
export function applyAppearanceToRenderer(
  target: AppearanceTarget | null | undefined,
  state: AppearanceState,
): void {
  if (!target) return;
  target.changeTreeLayout?.(state.layout);
  target.setNodeShape?.(state.nodeShape);
  target.setFontFamily?.(state.fontFamily);
  target.setFontColour?.(state.fontColour);
  target.setHaloColour?.(state.haloColour);
  target.setHaloRadius?.(state.haloRadius);
  target.setHaloWidth?.(state.haloWidth);
  target.setPadding?.(state.padding);
  target.setBlockSize?.(state.blockSize);
  target.setScalebarDigits?.(state.scalebarDigits);
  target.setScalebarWidth?.(state.scalebarWidth);
  target.setScalebarFontSize?.(state.scalebarFontSize);
  target.setScalebarOpacity?.(state.scalebarOpacity);
  target.setScalebarColour?.(state.scalebarColour);
  target.setNodeSize?.(state.nodeSize);
  target.setLabelSize?.(state.labelSize);
  target.setBranchZoom?.(state.branchZoom);
  target.setStepZoom?.(state.stepZoom);
  target.toggleLeafLabels?.(state.leafLabels);
  target.toggleLabelAlignment?.(state.alignLabels);
  target.toggleInternalLabels?.(state.internalLabels);
  target.setInternalLabelSize?.(state.internalLabelSize);
  target.setInternalLabelFilter?.(state.internalLabelMin, state.internalLabelMax);
  target.toggleColourEdges?.(state.colourEdges);
  target.toggleLeafBorders?.(state.leafBorders);
  target.toggleScalebar?.(state.scalebar);
  target.togglePiecharts?.(state.showPiecharts);
}

/**
 * Decide what the panel should do when the tree (re)renders. Returns the state
 * to reflect in the panel inputs, and restores it onto the fresh renderer *only*
 * when the user has actually saved an appearance. A renderer the user never
 * customised is left at its own defaults, so the out-of-the-box render is
 * byte-for-byte unchanged, the panel never perturbs default behaviour.
 */
export function restoreAppearanceOnRender(
  saved: unknown,
  target: AppearanceTarget | null | undefined,
  available?: readonly string[],
): AppearanceState {
  const state = mergeAppearance(saved, available);
  if (saved != null) {
    applyAppearanceToRenderer(target, state);
  }
  return state;
}
